<template>
  <div :class="variant === 'sidebar' ? 'nbell-row' : 'nbell-compact'">
    <v-menu
      v-model="menu"
      :offset-x="variant === 'sidebar'"
      :offset-y="variant !== 'sidebar'"
      :nudge-right="variant === 'sidebar' ? 8 : 0"
      :close-on-content-click="false"
      max-width="400"
      min-width="340"
      content-class="nbell-menu"
    >
      <template v-slot:activator="{ on, attrs }">
        <button
          type="button"
          class="nbell-btn"
          :class="{ 'nbell-btn--sidebar': variant === 'sidebar' }"
          v-on="on"
          v-bind="attrs"
        >
          <span class="nbell-iconwrap">
            <hero-icon :icon="BellIcon" :size="20" />
            <span v-if="unseenCount" class="nbell-badge">{{ unseenCount > 9 ? "9+" : unseenCount }}</span>
          </span>
          <span v-if="variant === 'sidebar'" class="nbell-label">Notificaciones</span>
        </button>
      </template>

      <div class="nbell-panel">
        <div class="nbell-panel__head">
          <span class="nbell-panel__title">Notificaciones</span>
          <span class="nbell-panel__sub">consultas y respuestas de clientes</span>
        </div>

        <div v-if="!items.length" class="nbell-empty">Sin novedades por ahora.</div>

        <div v-else class="nbell-list">
          <button
            v-for="n in items"
            :key="n.id"
            type="button"
            class="nbell-item"
            :class="{ 'nbell-item--unseen': n.unseen }"
            @click="openInquiry(n)"
          >
            <span class="nbell-item__icon" :class="'nbell-item__icon--' + n.kind">
              <hero-icon :icon="n.kind === 'client_reply' ? ReplyIcon : InboxIcon" :size="15" />
            </span>
            <span class="nbell-item__body">
              <span class="nbell-item__top">
                <span class="nbell-item__name">{{ n.name }}</span>
                <span class="nbell-item__time">{{ timeAgo(n.at) }}</span>
              </span>
              <span class="nbell-item__what">
                {{ n.kind === "client_reply" ? "respondió en su consulta" : "nueva consulta" }}
                <template v-if="n.propertyLabel"> — {{ n.propertyLabel }}</template>
              </span>
              <span v-if="n.excerpt" class="nbell-item__excerpt">{{ n.excerpt }}</span>
            </span>
            <span v-if="n.unseen" class="nbell-item__dot"></span>
          </button>
        </div>

        <button type="button" class="nbell-panel__all" @click="goToConsultas">
          Ver todas las consultas
        </button>
      </div>
    </v-menu>

    <!-- opening a notification jumps straight into that inquiry's panel -->
    <inquiry-detail-dialog ref="detail" />
  </div>
</template>

<script>
import { NotificationsFeed } from "@/graphql/queries/inquiries";
import { MarkNotificationsSeen } from "@/graphql/mutations/inquiries";
import { subscribeInquiries } from "@/services/inquiriesCable";
import HeroIcon from "@/components/shared/HeroIcon";
import InquiryDetailDialog from "@/components/inquiries/InquiryDetailDialog";
import BellIcon from "heroicons/24/outline/bell.svg";
import InboxIcon from "heroicons/24/outline/inbox-arrow-down.svg";
import ReplyIcon from "heroicons/24/outline/chat-bubble-left-ellipsis.svg";

// cable events drive the bell now; the poll is just a reconnection safety net
const POLL_MS = 180000;

export default {
  name: "NotificationsBell",

  props: {
    variant: { type: String, default: "sidebar" } // sidebar | compact
  },

  data: () => ({
    menu: false,
    items: [],
    unseenCount: 0,
    timer: null,
    BellIcon,
    InboxIcon,
    ReplyIcon
  }),

  created() {
    this.fetchFeed();
    this.timer = setInterval(this.fetchFeed, POLL_MS);
    // live: a new consulta or a client reply lands in the feed instantly
    this._sub = subscribeInquiries(e => {
      if (
        e.type === "inquiryCreated" ||
        e.type === "inquiryUpdated" ||
        (e.type === "comment" && e.comment && e.comment.fromClient)
      ) {
        this.fetchFeed();
      }
    });
  },

  beforeDestroy() {
    clearInterval(this.timer);
    if (this._sub) this._sub.unsubscribe();
  },

  watch: {
    // opening the bell clears the badge (rows keep their highlight this session)
    menu(open) {
      if (open && this.unseenCount) this.markSeen();
    }
  },

  methods: {
    fetchFeed() {
      this.$apollo
        .query({
          query: NotificationsFeed,
          variables: { limit: 15 },
          fetchPolicy: "no-cache"
        })
        .then(res => {
          this.items = res.data.notificationsFeed || [];
          this.unseenCount = this.items.filter(n => n.unseen).length;
        })
        .catch(() => {});
    },

    markSeen() {
      this.unseenCount = 0;
      this.$apollo
        .mutate({ mutation: MarkNotificationsSeen, variables: { input: {} } })
        .catch(() => {});
    },

    openInquiry(n) {
      this.menu = false;
      this.$refs.detail.open(n.inquiryId);
    },

    goToConsultas() {
      this.menu = false;
      this.$router.push({ name: "Consultas" }).catch(() => {});
    },

    timeAgo(iso) {
      if (!iso) return "";
      const m = Math.floor(Math.max(0, Date.now() - new Date(iso).getTime()) / 60000);
      if (m < 1) return "recién";
      if (m < 60) return `hace ${m} min`;
      const h = Math.floor(m / 60);
      if (h < 24) return `hace ${h} h`;
      const d = Math.floor(h / 24);
      return `hace ${d} día${d === 1 ? "" : "s"}`;
    }
  },

  components: { HeroIcon, InquiryDetailDialog }
};
</script>

<style scoped>
.nbell-row { padding: 6px 8px 2px; }
.nbell-compact { display: inline-flex; margin-right: 4px; }

.nbell-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  background: none;
  border: none;
  cursor: pointer;
  color: #56504a;
  padding: 8px;
  border-radius: 10px;
  width: auto;
}
.nbell-btn--sidebar { width: 100%; padding: 9px 12px; }
.nbell-btn:hover { background: #f4f1ea; color: #1a1814; }

.nbell-iconwrap { position: relative; display: inline-flex; }
.nbell-badge {
  position: absolute;
  top: -6px;
  right: -8px;
  min-width: 16px;
  height: 16px;
  border-radius: 999px;
  background: #b52118;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
}
.nbell-label { font-size: 14px; font-weight: 500; }

/* panel */
.nbell-panel { background: #fff; }
.nbell-panel__head {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 13px 16px 10px;
  border-bottom: 1px solid #ece7de;
}
.nbell-panel__title { font-size: 14px; font-weight: 700; color: #1a1814; }
.nbell-panel__sub { font-size: 11px; color: #a39a8c; }

.nbell-empty { padding: 28px 16px; text-align: center; font-size: 13px; color: #a39a8c; }

.nbell-list { max-height: 420px; overflow-y: auto; }
.nbell-item {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  border-bottom: 1px solid #f1ece3;
  padding: 11px 14px;
  cursor: pointer;
  font: inherit;
}
.nbell-item:hover { background: #faf8f4; }
.nbell-item--unseen { background: #fdf6ee; }
.nbell-item--unseen:hover { background: #fbf0e2; }

.nbell-item__icon {
  flex: none;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f4f1ea;
  color: #756f64;
  margin-top: 2px;
}
.nbell-item__icon--client_reply { background: #fdeed3; color: #92400e; }

.nbell-item__body { min-width: 0; flex: 1; display: flex; flex-direction: column; gap: 1px; }
.nbell-item__top { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; }
.nbell-item__name { font-size: 13px; font-weight: 700; color: #1a1814; }
.nbell-item__time { flex: none; font-size: 10.5px; color: #a39a8c; }
.nbell-item__what { font-size: 12px; color: #56504a; }
.nbell-item__excerpt {
  font-size: 12px;
  color: #a39a8c;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-style: italic;
}
.nbell-item__dot { flex: none; width: 8px; height: 8px; border-radius: 50%; background: #b52118; margin-top: 8px; }

.nbell-panel__all {
  display: block;
  width: 100%;
  background: none;
  border: none;
  border-top: 1px solid #ece7de;
  padding: 11px;
  font: inherit;
  font-size: 12.5px;
  font-weight: 600;
  color: #b52118;
  cursor: pointer;
}
.nbell-panel__all:hover { background: #faf8f4; }
</style>

<style>
/* unscoped: v-menu content renders at root */
.nbell-menu {
  border-radius: 14px !important;
  border: 1px solid #ece7de;
  box-shadow: 0 18px 50px -18px rgba(40, 33, 22, 0.35) !important;
}
</style>
